/**
 * sandboxDetect — utilities for spotting the Emergent preview iframe.
 *
 * The Emergent chat UI hosts the app inside an iframe whose sandbox flags
 * don't include `allow-downloads`, so the browser silently swallows any
 * Save-As / download attempt — even popup-opened wrapper pages inherit the
 * restriction. Everything works correctly on the deployed site and when
 * the preview URL is opened in its own browser tab; only the embedded
 * preview pane is broken.
 *
 * Rather than chasing a code-level workaround that doesn't exist, we
 * detect this environment and surface a clear, actionable message.
 */

/**
 * True when we're rendered inside ANY iframe (cross-origin attempts to
 * read window.top throw — we treat that as "embedded too").
 */
export function isEmbedded() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * True when we appear to be inside the Emergent chat-UI preview pane.
 *
 * We check both that we're embedded AND that the URL ends in
 * `.preview.emergentagent.com` — that combo only ever happens inside the
 * Emergent product. Once a customer deploys to their own domain this
 * returns false and the normal download flow takes over.
 */
export function isInEmergentPreview() {
  if (!isEmbedded()) return false;
  try {
    return /\.preview\.emergentagent\.com$/i.test(window.location.hostname);
  } catch {
    return false;
  }
}

/**
 * Open the current app URL in a fresh top-level browser tab. Used by the
 * download banner so the user can escape the sandbox in one click.
 */
export function openPreviewInNewTab() {
  try {
    const url = window.location.href;
    window.open(url, '_blank');
  } catch {
    /* no-op */
  }
}
